// ═══════════════════════════════════════════════════════════════════════════
// dailyLogFeed.js — how the Daily Log treats rows that came from a feed
// ═══════════════════════════════════════════════════════════════════════════
// No React, no network, no storage. Used by pages/AdminDailyLog.js.
//
// A daily_logs row with source 'feed' was written by the integration service
// (migration 046): one row per order (046's daily_logs_feed_row_shape: a
// channel, orders = 1, the platform's order number as kot_number, the
// provider order id as external_ref; docs/integrations/feed-api-contract.md,
// open question 15). Even so, nothing here reads kot_number, orders or a
// provider: the badge and the lock depend on the row's source ONLY, so a row
// of any shape is treated the same. No feed API call, no /api/feed/enabled
// probe: fed rows exist only where the feed runs, and the database trigger
// (protect_feed_days, SQLSTATE NF001) is the real guard.
// ═══════════════════════════════════════════════════════════════════════════

// SQLSTATE raised by 046's protect_feed_days() (daily_logs) for every refusal,
// and by its protect_cofund_on_fed_days() (platform_cofunding: no Platform
// contribution over a live feed window). PostgREST answers 400 and
// supabase-js puts the SQLSTATE in error.code.
export const FEED_DAY_ERROR_CODE = "NF001";

// A row the integration service wrote.
export function isFedRow(row) {
  return row?.source === "feed";
}

// A write the one-source-per-channel-day trigger refused. Read by code only:
// the trigger has two messages (and names the provider in one), and its
// DETAIL carries ids, so neither the message nor the detail is ever shown.
export function isFedDayError(error) {
  return String(error?.code ?? "") === FEED_DAY_ERROR_CODE;
}

// Local YYYY-MM-DD of a Date, "" for anything else (the page's own ymd).
function ymd(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// The row's log date as YYYY-MM-DD. Rows loaded by db.js carry only dateObj
// (local midnight of log_date); rows added this session also carry `date`.
export function rowDay(row) {
  if (typeof row?.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(row.date)) return row.date;
  return ymd(row?.dateObj);
}

// Does this channel's history hold a fed row on that day? Typed rows can never
// join a fed day (the owner's rule, 21 Oct 2026). A fed day with no counting
// orders, or one whose rows haven't loaded yet, has no row to see, so the
// trigger's NF001 stays the backstop.
export function hasFedRowOnDay(history, day) {
  if (!day || !Array.isArray(history)) return false;
  return history.some((r) => isFedRow(r) && rowDay(r) === day);
}
